import { z } from 'zod';
/**
 * Admin app push contract — apps/api `modules/admin-mobile-push`
 * (admin-mobile-app phase 5, owner decision D-5).
 *
 * The push target and the per-device preferences live ON the device session
 * (`admin_device_sessions`): a session and its push token die together.
 *
 *   POST   /api/admin/mobile-sessions/me/push-token  {token}   → {registered: true} | 409 PUSH_TOKEN_IN_USE
 *   DELETE /api/admin/mobile-sessions/me/push-token           → {registered: false}
 *   GET    /api/admin/mobile-sessions/me/preferences          → AdminPushPreferences
 *   PUT    /api/admin/mobile-sessions/me/preferences  body    → AdminPushPreferences
 *
 * All four need the admin app's own access token (`me` is the token's device
 * session) and answer 503 `ADMIN_MOBILE_PUSH_DISABLED` while
 * `ADMIN_MOBILE_PUSH_ENABLED` is off.
 */
/** What an operator can mute per device. The critical escalation has no topic and cannot be muted. */
export const AdminPushTopicSchema = z.enum(['relay', 'kyc', 'finance']);
export const ADMIN_PUSH_TOPICS = AdminPushTopicSchema.options;
export const AdminPushTopicsSchema = z.object({
    relay: z.boolean(),
    kyc: z.boolean(),
    finance: z.boolean(),
});
/** Every topic on — what a device that never saved preferences gets. */
export const DEFAULT_ADMIN_PUSH_TOPICS = { relay: true, kyc: true, finance: true };
const HH_MM = /^([01]\d|2[0-3]):[0-5]\d$/;
/**
 * Quiet hours in the device's own IANA zone. `start > end` crosses midnight
 * (22:00 → 07:00); `start === end` is an empty window (never quiet).
 */
export const AdminPushQuietHoursSchema = z.object({
    start: z.string().regex(HH_MM, 'HH:mm'),
    end: z.string().regex(HH_MM, 'HH:mm'),
    tz: z.string().trim().min(1).max(64),
});
/** GET/PUT 200 body. */
export const AdminPushPreferencesSchema = z.object({
    topics: AdminPushTopicsSchema,
    quietHours: AdminPushQuietHoursSchema.nullable(),
    /** Whether this device session currently has a push token registered. */
    pushTokenRegistered: z.boolean(),
});
/** PUT body. `quietHours: null` clears them; omitted leaves them as they are. */
export const UpdateAdminPushPreferencesSchema = z
    .object({
    topics: AdminPushTopicsSchema.partial().optional(),
    quietHours: AdminPushQuietHoursSchema.nullable().optional(),
})
    .strict();
/** POST push-token body — an Expo push token (`ExponentPushToken[…]`). */
export const AdminPushTokenRequestSchema = z.object({
    token: z.string().trim().min(1).max(200),
});
export const AdminMobilePushErrorCodeSchema = z.enum([
    'VALIDATION_FAILED',
    /** `ADMIN_MOBILE_PUSH_ENABLED` is off (503). */
    'ADMIN_MOBILE_PUSH_DISABLED',
    /** The caller is not the admin app (no device session behind the request) (403). */
    'ADMIN_MOBILE_SESSION_REQUIRED',
    /** Not an Expo push token (400). */
    'INVALID_PUSH_TOKEN',
    /** The device session vanished between the guard and the write (404). */
    'SESSION_NOT_FOUND',
    /** The token is registered to ANOTHER admin's active session — it is not moved (409). */
    'PUSH_TOKEN_IN_USE',
]);
//# sourceMappingURL=admin-mobile-push.js.map